// The live run plan (UX slice S4): the backend's `data.run_plan` (once per capability) and the
// `data.plan_step` / `data.plan_skipped` every later frame carries, read defensively. The panel itself
// is ExportDesignRunPanel.tsx (a distinct name on purpose, plan §0.5).

export interface RunPlanStep {
  id: string;
  label: string;
  /** Shown beside a finished step ("4 specs noted"). */
  meta?: string;
  /** What the step is, shown under it while it runs (S12 R-8: what the second review does). */
  hint?: string;
}

export interface RunPlan {
  capability: string;
  title: string;
  typical: string;
  steps: RunPlanStep[];
  /** The panel's foot line (S5: "You'll see the ITAR answer as soon as it's ready…"). */
  note?: string;
}

export type RunStepStatus = 'done' | 'active' | 'pending' | 'skipped';

const isString = (value: unknown): value is string => typeof value === 'string';

/** `data.run_plan`, or undefined when absent or unusable (the chat then keeps today's Thinking panel). */
export const readRunPlan = (input: unknown): RunPlan | undefined => {
  const raw = input as any;
  if (!raw || typeof raw !== 'object' || !isString(raw.title) || !raw.title.trim()) return undefined;
  const steps = (Array.isArray(raw.steps) ? raw.steps : [])
    .filter((s: any) => s && isString(s.id) && isString(s.label) && s.label.trim())
    .map((s: any): RunPlanStep => {
      const step: RunPlanStep = { id: s.id, label: s.label };
      if (isString(s.meta) && s.meta) step.meta = s.meta;
      if (isString(s.hint) && s.hint.trim()) step.hint = s.hint;
      return step;
    });
  if (!steps.length) return undefined;
  const plan: RunPlan = {
    capability: isString(raw.capability) ? raw.capability : '',
    title: raw.title,
    typical: isString(raw.typical) ? raw.typical : '',
    steps: steps,
  };
  if (isString(raw.note) && raw.note.trim()) plan.note = raw.note;
  return plan;
};

/** `data.plan_skipped`: {step id: reason}. */
export const readPlanSkipped = (input: unknown): Record<string, string> => {
  const raw = input as any;
  const out: Record<string, string> = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw).forEach((key) => {
    if (isString(raw[key])) out[key] = raw[key];
  });
  return out;
};

/**
 * Each step's status. Steps before the current one are done (a step passed without being entered
 * counts as done), unless skipped; the current one is active; the rest are pending. With no current
 * step yet, the first one that isn't skipped is active.
 */
export const runStepStatuses = (
  plan: RunPlan,
  current: string | null | undefined,
  skipped: Record<string, string>,
): RunStepStatus[] => {
  const ids = plan.steps.map((s) => s.id);
  let at = current ? ids.indexOf(current) : -1;
  if (at < 0) at = ids.findIndex((id) => !skipped[id]);
  return ids.map((id, index) => {
    if (skipped[id]) return 'skipped';
    if (index < at) return 'done';
    if (index === at) return 'active';
    return 'pending';
  });
};

/** "0:41", "2:05": the panel's clocks. */
export const formatClock = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
